// Story 9.6 (ADO-1080), AC10 — the calendar arithmetic behind the design-system Date
// Picker. Pure functions, no React, so the month grid and the bounds logic are testable
// without rendering anything.
//
// THE DAY IS A STRING. Every date in this component, its props, the query string and
// the API contract is an ISO calendar day, `YYYY-MM-DD` — never a `Date`. Three
// reasons, in order of how much trouble each avoids:
//
//   1. A `Date` is an instant, and a day is not. Holding "21 February 2026" as a `Date`
//      forces a zone choice at every hand-off, and the classic bug (a day that becomes
//      the day before when it crosses a boundary) is exactly what a date FILTER must
//      never do.
//   2. ISO day strings compare correctly with `<` and `>`, because the format is
//      fixed-width and big-endian. So the min/max clamping and the "is this day inside
//      the chosen range" test are string comparisons with nothing to get wrong.
//   3. It is what the URL carries, so the address bar stays readable and a pasted link
//      restores the picker without a conversion.
//
// A `Date` appears in exactly three places, and each is deliberate. The grid maths below
// build one with `Date.UTC` and read it back with `getUTC*`, so that arithmetic is
// zone-free by construction. `todayIsoDay` asks the machine what day it is LOCALLY,
// because the ring marks the viewer's today, not UTC's. And `localDay{Start,End}Instant`
// resolve a day into the instants it spans in the viewer's zone — that is the conversion
// the API client makes on the way out, and the reason the filter selects the same day
// the list displays.

/** A calendar day as `YYYY-MM-DD`. Not an instant: see the note above. */
export type IsoDay = string;

const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Weekday headers, Monday-first (AC10). Index 0 is Monday, matching `weekdayIndex`. */
export const WEEKDAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;

/** Full weekday names, for the accessible name of a day cell. */
const WEEKDAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** A year + 0-based month, the unit the grid is drawn for. */
export interface CalendarMonth {
  readonly year: number;
  readonly month: number;
}

function pad(value: number, width = 2): string {
  return String(value).padStart(width, '0');
}

/**
 * True when `value` is a well-formed, REAL calendar day.
 *
 * The shape check alone is not enough: `2026-02-31` matches the pattern and `Date.UTC`
 * would happily roll it into March. The components are read back off the constructed
 * instant and required to match, so a day that does not exist is rejected rather than
 * silently becoming a different one. The server applies the same round-trip check to the
 * instants it receives (`parseIsoInstant` in `order-history-query.validation.ts`), so a
 * value that is not a real date is refused at both ends.
 */
export function isIsoDay(value: string): value is IsoDay {
  if (!ISO_DAY_RE.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number) as [number, number, number];
  const instant = new Date(Date.UTC(year, month - 1, day));
  return (
    instant.getUTCFullYear() === year &&
    instant.getUTCMonth() === month - 1 &&
    instant.getUTCDate() === day
  );
}

/** Build an {@link IsoDay} from its parts. `month` is 0-based, as `Date` uses it. */
export function isoDay(year: number, month: number, day: number): IsoDay {
  return `${pad(year, 4)}-${pad(month + 1)}-${pad(day)}`;
}

/** The month an {@link IsoDay} falls in, or `null` when it is not a real day. */
export function monthOf(day: IsoDay): CalendarMonth | null {
  if (!isIsoDay(day)) return null;
  const [year, month] = day.split('-').map(Number) as [number, number];
  return { year, month: month - 1 };
}

/** `Month YYYY`, the Date Picker's header (Figma 4919:1533). */
export function formatMonthHeading(month: CalendarMonth): string {
  return `${MONTH_NAMES[month.month] ?? ''} ${month.year}`;
}

/**
 * `DD/MM/YYYY` — what the date FIELD shows (Figma 4925:50656), matching how the order
 * rows already render their submitted date. An unparseable value comes back unchanged
 * rather than being rendered as some other date.
 */
export function formatDisplayDay(day: string): string {
  if (!isIsoDay(day)) return day;
  const [year, month, dayOfMonth] = day.split('-');
  return `${dayOfMonth}/${month}/${year}`;
}

/** The full accessible name of a day cell, e.g. "Saturday 21 February 2026". */
export function formatDayLabel(day: IsoDay): string {
  const parts = monthOf(day);
  if (parts === null) return day;
  const dayOfMonth = Number(day.slice(8, 10));
  const weekday = WEEKDAY_NAMES[weekdayIndex(day)] ?? '';
  return `${weekday} ${dayOfMonth} ${MONTH_NAMES[parts.month] ?? ''} ${parts.year}`;
}

/** Monday-first weekday index of a day: 0 = Monday … 6 = Sunday. */
export function weekdayIndex(day: IsoDay): number {
  const [year, month, dayOfMonth] = day.split('-').map(Number) as [number, number, number];
  // `getUTCDay` is Sunday-first (0 = Sunday); rotate it so Monday is 0 (AC10).
  return (new Date(Date.UTC(year, month - 1, dayOfMonth)).getUTCDay() + 6) % 7;
}

/** The day `offset` days after `day` (negative moves back). */
export function addDays(day: IsoDay, offset: number): IsoDay {
  const [year, month, dayOfMonth] = day.split('-').map(Number) as [number, number, number];
  const moved = new Date(Date.UTC(year, month - 1, dayOfMonth + offset));
  return isoDay(moved.getUTCFullYear(), moved.getUTCMonth(), moved.getUTCDate());
}

/**
 * The month `offset` months after `month`. Used by the header's prev/next and by
 * PageUp/PageDown; it moves the MONTH, never a day, so there is no end-of-month
 * clamping question to get wrong here.
 */
export function addMonths(month: CalendarMonth, offset: number): CalendarMonth {
  const moved = new Date(Date.UTC(month.year, month.month + offset, 1));
  return { year: moved.getUTCFullYear(), month: moved.getUTCMonth() };
}

/**
 * The same day in another month, pulled back to that month's last day when it does not
 * exist there (31 January + 1 month is 28 February, not 3 March). This is what
 * PageUp/PageDown move the FOCUSED day by, and a silent roll-over would jump the focus
 * two months and leave the grid showing one.
 */
export function addMonthsToDay(day: IsoDay, offset: number): IsoDay {
  const parts = monthOf(day);
  if (parts === null) return day;
  const target = addMonths(parts, offset);
  const dayOfMonth = Math.min(Number(day.slice(8, 10)), daysInMonth(target));
  return isoDay(target.year, target.month, dayOfMonth);
}

/** How many days the month has. */
export function daysInMonth(month: CalendarMonth): number {
  // Day 0 of the NEXT month is the last day of this one.
  return new Date(Date.UTC(month.year, month.month + 1, 0)).getUTCDate();
}

/**
 * The month laid out as Monday-first weeks (AC10). Each cell is an {@link IsoDay} or
 * `null` for the blanks before the first and after the last of the month, which the
 * frame draws as empty 44×44 boxes rather than as the neighbouring months' days
 * (Figma 4919:1553 / 4919:1618).
 */
export function monthGrid(month: CalendarMonth): (IsoDay | null)[][] {
  const total = daysInMonth(month);
  const leading = weekdayIndex(isoDay(month.year, month.month, 1));
  const cells: (IsoDay | null)[] = [
    ...Array.from({ length: leading }, () => null),
    ...Array.from({ length: total }, (unused, index) => isoDay(month.year, month.month, index + 1)),
  ];
  // Pad the last week out to seven so every row is a complete 7-cell grid row: an
  // assistive technology reading a `role="grid"` expects rows of equal length.
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (IsoDay | null)[][] = [];
  for (let start = 0; start < cells.length; start += 7) {
    weeks.push(cells.slice(start, start + 7));
  }
  return weeks;
}

/**
 * Whether `day` is selectable given the inclusive bounds. AC9's UI half rests on this:
 * the From picker is capped at the chosen To and the To picker floored at the chosen
 * From, so an impossible range cannot be entered in the first place.
 */
export function isWithinBounds(day: IsoDay, min?: string, max?: string): boolean {
  if (min !== undefined && min !== '' && day < min) return false;
  if (max !== undefined && max !== '' && day > max) return false;
  return true;
}

/**
 * Whether `day` falls strictly between the two ends of the chosen range — the days that
 * get the AC10 range wash. Strictly, because the ends themselves are drawn as the
 * selected day in their own picker and would otherwise be painted twice.
 */
export function isInsideRange(day: IsoDay, start?: string | null, end?: string | null): boolean {
  if (start === undefined || start === null || start === '') return false;
  if (end === undefined || end === null || end === '') return false;
  return day > start && day < end;
}

/**
 * The viewer's own today, as an {@link IsoDay} — what the "today" ring marks.
 *
 * Read from the local clock, not UTC: "today" on a calendar is the day on the wall,
 * the same reading `formatSubmittedDate` already takes for the submitted-at column. The
 * fields are pulled individually rather than off `toISOString`, which would answer
 * for UTC and so show the wrong ring for part of every day east or west of it.
 */
export function todayIsoDay(now: Date): IsoDay {
  return isoDay(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * The instant a calendar day BEGINS in the viewer's own zone, as a UTC ISO 8601 string.
 *
 * This is what turns a picked day into a filter bound, and it is deliberately LOCAL.
 * The order list renders each submitted date in the viewer's zone (Story 9.4, PO
 * 2026-09-18), so "from the 21st" has to mean the 21st *as the screen shows it*. Reading
 * the day as a UTC day instead is off by the zone's offset, and the orders that fall in
 * that gap are exactly the ones a reader can see on screen dated the 21st — which is the
 * worst possible set to silently exclude. Verified against real data: an order stored at
 * `2026-09-20T23:30Z` renders as `21/09/2026 01:30` at UTC+2 and was dropped by a
 * "from 2026-09-21" filter until this became local.
 *
 * Built with the LOCAL `Date` constructor, then serialised with `toISOString()`, so the
 * zone conversion is the platform's and includes DST. An unparseable day returns `null`
 * rather than a fabricated instant.
 */
export function localDayStartInstant(day: string): string | null {
  const parts = monthOf(day);
  if (parts === null) return null;
  return new Date(parts.year, parts.month, Number(day.slice(8, 10)), 0, 0, 0, 0).toISOString();
}

/**
 * The LAST instant of a calendar day in the viewer's own zone, as a UTC ISO 8601 string —
 * the inclusive upper bound AC1 asks for. Taking the day at midnight instead would
 * exclude everything actually sent during it.
 */
export function localDayEndInstant(day: string): string | null {
  const parts = monthOf(day);
  if (parts === null) return null;
  return new Date(parts.year, parts.month, Number(day.slice(8, 10)), 23, 59, 59, 999).toISOString();
}
